import { QueryClient, useQuery } from "@tanstack/react-query";
import type React from "react";

import { fetchUser } from "~/routes/login._index/fetcher";

import type { User } from "~/services/api-schemas";

import { MINUTE_IN_MILLIS } from "@nephroflow/design-system/utils/date";
import { invariant } from "@nephroflow/design-system/utils/invariant";
import { ADMINISTRATOR_ROLE, TECHNICIAN_ROLE } from "~/utils/constants";

import { Loading } from "~/components/loading";

export const CURRENT_USER_QUERY_KEY = ["currentUser"];

export type ExtendedUser = User & {
  isAdmin: boolean;
  isTechnician: boolean;
  emailSubscribedDeviceIds: string[] | undefined;
  smsSubscribedDeviceIds: string[] | undefined;
};

export function CurrentUserProvider({ children }: { children: React.ReactElement<any> }) {
  // Refetch on window focus after 5 minutes of stale data
  const { isPending: isLoading, data: user } = useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => fetchUser(),
    refetchOnWindowFocus: true,
    staleTime: 5 * MINUTE_IN_MILLIS,
  });

  if (isLoading || !user) {
    return <Loading.Logo className="h-screen" />;
  }

  return children;
}

export function useCurrentUser({ refetchOnMount }: { refetchOnMount?: boolean | "always" } = {}) {
  // Refetch on hook mount after 30 minutes of stale data
  const { data: user } = useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => fetchUser(),
    staleTime: 30 * MINUTE_IN_MILLIS,
    refetchOnMount,
  });

  invariant(user, "The hook `useCurrentUser` must be used within a `CurrentUserProvider`.");

  const extendedUser: ExtendedUser = {
    ...user,
    isAdmin: user?.role === ADMINISTRATOR_ROLE,
    isTechnician: user?.role === TECHNICIAN_ROLE,
    emailSubscribedDeviceIds: user.emailSubscribedDeviceIds || [],
    smsSubscribedDeviceIds: user.smsSubscribedDeviceIds || [],
  };
  return extendedUser;
}

export function refetchCurrentUser(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
}
